import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Package, Truck, GraduationCap, ClipboardText, Gauge,
} from "@phosphor-icons/react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { getAdminLoads, getSubmissions, updateLoadStatus } from "@/lib/api";

const TYPE_META = {
  quote: { label: "Quote", icon: Package, color: "text-[#2563EB]" },
  carrier: { label: "Carrier", icon: Truck, color: "text-[#60A5FA]" },
  startup_consult: { label: "Consult", icon: GraduationCap, color: "text-amber-400" },
};

export default function Admin() {
  const [subs, setSubs] = useState({ leads: [], load_requests: [], counts: {} });
  const [loads, setLoads] = useState([]);

  const refresh = useCallback(() => {
    getSubmissions().then(setSubs).catch(() => {});
    getAdminLoads().then(setLoads).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const changeStatus = async (id, status) => {
    try {
      await updateLoadStatus(id, status);
      setLoads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success(`Load marked ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const c = subs.counts || {};
  const KPIS = [
    { label: "Quotes", value: c.quotes || 0, icon: Package },
    { label: "Carrier Signups", value: c.carriers || 0, icon: Truck },
    { label: "Consults", value: c.consults || 0, icon: GraduationCap },
    { label: "Load Requests", value: c.load_requests || 0, icon: ClipboardText },
    { label: "Active Loads", value: loads.filter((l) => l.status === "open").length, icon: Gauge },
  ];

  return (
    <div className="min-h-screen bg-[#0B1220] text-white">
      {/* Top bar */}
      <header className="glass border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/cel-horizontal.png" alt="Coastal Expedited Logistics" className="h-8 w-auto" />
            <span className="font-display text-[#94A3B8] font-normal border-l border-white/15 pl-3">Admin</span>
          </Link>
          <Link to="/loads" className="text-sm text-[#94A3B8] hover:text-[#2563EB]">View Load Board →</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-8">Control Tower</h1>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {KPIS.map((k) => (
            <div key={k.label} className="bg-[#111A2E] border border-white/10 rounded-xl p-5" data-testid={`kpi-${k.label}`}>
              <k.icon size={22} className="text-[#2563EB] mb-3" />
              <div className="font-display text-3xl font-extrabold tracking-tighter">{k.value}</div>
              <div className="text-xs text-[#94A3B8] mt-1">{k.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="loads">
          <TabsList className="bg-[#111A2E] border border-white/10 mb-6">
            <TabsTrigger value="loads" data-testid="admin-tab-loads" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">New Loads</TabsTrigger>
            <TabsTrigger value="requests" data-testid="admin-tab-requests" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Load Requests</TabsTrigger>
            <TabsTrigger value="submissions" data-testid="admin-tab-submissions" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">Submissions</TabsTrigger>
          </TabsList>

          {/* LOADS */}
          <TabsContent value="loads">
            <div className="bg-[#111A2E] border border-white/10 rounded-xl overflow-hidden">
              <table className="w-full text-sm" data-testid="admin-loads-table">
                <thead className="bg-white/5 text-[#94A3B8] text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3">Lane</th>
                    <th className="text-left px-5 py-3 hidden md:table-cell">Equipment</th>
                    <th className="text-left px-5 py-3 hidden lg:table-cell">Pickup</th>
                    <th className="text-right px-5 py-3">Rate</th>
                    <th className="text-left px-5 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loads.map((l) => (
                    <tr key={l.id} className="border-t border-white/5" data-testid={`admin-load-row-${l.id}`}>
                      <td className="px-5 py-4 font-medium">{l.origin_city}, {l.origin_state} → {l.dest_city}, {l.dest_state}</td>
                      <td className="px-5 py-4 hidden md:table-cell text-[#94A3B8]">{l.equipment}</td>
                      <td className="px-5 py-4 hidden lg:table-cell text-[#94A3B8]">{l.pickup_date}</td>
                      <td className="px-5 py-4 text-right font-bold text-[#10B981]">${l.rate_usd.toLocaleString()}</td>
                      <td className="px-5 py-4">
                        <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                          <SelectTrigger data-testid={`admin-status-select-${l.id}`} className="w-32 bg-[#0B1220] border-white/10 h-9"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-[#111A2E] border-white/10 text-white">
                            <SelectItem value="open">Open</SelectItem>
                            <SelectItem value="booked">Booked</SelectItem>
                            <SelectItem value="closed">Closed</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {loads.length === 0 && <div className="p-10 text-center text-[#94A3B8]">No loads yet.</div>}
            </div>
          </TabsContent>

          {/* REQUESTS */}
          <TabsContent value="requests">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="admin-requests-list">
              {subs.load_requests.map((r) => (
                <div key={r.id} className="bg-[#111A2E] border border-white/10 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3"><Truck size={18} className="text-[#2563EB]" /><span className="font-display font-bold">{r.carrier_name}</span>{r.mc_number && <span className="text-xs text-[#94A3B8]">MC {r.mc_number}</span>}</div>
                  <div className="text-sm text-[#94A3B8] space-y-1">
                    <div>{r.email} {r.phone && `· ${r.phone}`}</div>
                    {r.message && <div className="text-[#cbd5e1] italic">"{r.message}"</div>}
                  </div>
                </div>
              ))}
            </div>
            {subs.load_requests.length === 0 && <div className="p-10 text-center text-[#94A3B8] bg-[#111A2E] border border-white/10 rounded-xl">No load requests yet.</div>}
          </TabsContent>

          {/* SUBMISSIONS */}
          <TabsContent value="submissions">
            <div className="space-y-3" data-testid="admin-submissions-list">
              {subs.leads.map((l) => {
                const meta = TYPE_META[l.type] || TYPE_META.quote;
                return (
                  <div key={l.id} className="bg-[#111A2E] border border-white/10 rounded-xl p-5 flex flex-col md:flex-row md:items-center gap-4">
                    <div className={`flex items-center gap-2 md:w-40 shrink-0 ${meta.color}`}><meta.icon size={18} /><span className="text-xs uppercase tracking-wider font-semibold">{meta.label}</span></div>
                    <div className="flex-1">
                      <div className="font-display font-bold">{l.name} <span className="text-[#94A3B8] font-normal text-sm">· {l.email} {l.phone && `· ${l.phone}`}</span></div>
                      {Object.keys(l.payload || {}).length > 0 && (
                        <div className="text-xs text-[#94A3B8] mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                          {Object.entries(l.payload).map(([k, v]) => <span key={k}><span className="text-[#64748b]">{k.replace(/_/g, " ")}:</span> {String(v)}</span>)}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {subs.leads.length === 0 && <div className="p-10 text-center text-[#94A3B8] bg-[#111A2E] border border-white/10 rounded-xl">No submissions yet.</div>}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
